import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "../environments/environment";
import { catchError } from "rxjs/operators";
import { of, throwError, OperatorFunction } from "rxjs";

const catchEmpty200 = <T = unknown>(): OperatorFunction<T, T> => {
  return catchError((err) => {
    // Catch for when api sends us empty 200
    if (err.status === 200 && err.statusText === "OK") {
      return of(undefined as unknown as T);
    }
    return throwError(() => err);
  });
};

@Injectable({
  providedIn: "root",
})
export class ApiService {
  constructor(private http: HttpClient) {}

  private static formData(data: Object) {
    const form = new FormData();
    for (const key in data) {
      form.append(key, (data as any)[key]);
    }
    return form;
  }

  private buildOptions(options?: any) {
    const base = { ...(options || {}) } as any;
    // Always enforce observe: 'body' to ensure Observable<T> return type
    base.observe = 'body';
    if (typeof base.responseType === 'undefined') {
      base.responseType = 'json';
    }
    return base;
  }

  post<T>(path: string, body: any, options?: any): Observable<T> {
    return this.http.post(`${environment.apiURL}/${path}`, body, this.buildOptions(options)) as unknown as Observable<T>;
  }

  get<T>(path: string, options?: any): Observable<T> {
    return this.http.get(`${environment.apiURL}/${path}`, this.buildOptions(options)) as unknown as Observable<T>;
  }

  put<T>(path: string, body: any, options?: any): Observable<T> {
    return this.http.put(`${environment.apiURL}/${path}`, body, this.buildOptions(options)) as unknown as Observable<T>;
  }

  delete<T>(path: string, options?: any): Observable<T> {
    return (this.http
      .delete(`${environment.apiURL}/${path}`, this.buildOptions(options)) as unknown as Observable<T>)
      .pipe(catchEmpty200<T>());
  }

  download(path: string, filename?: string) {
    const url = `${environment.apiURL}/${path}`;
    this.http.get(url, { responseType: 'blob' as 'json' }).subscribe((blob: any) => {
      const objectUrl = window.URL.createObjectURL(blob as any);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename || 'download.zip';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objectUrl);
    });
  }

  currentWeatherData(zipCode?: string, type?: string) {
    const units = type === "F" ? "imperial" : "metric";
    return this.http.get<any>(
      `https://api.openweathermap.org/data/2.5/weather?zip=${zipCode}&appid=${environment.openWeatherMapAPIKey}&units=${units}
    `,
      { headers: { skipIntercept: "true" } },
    );
  }
}
